/* eslint-disable max-lines */
import React, { PropsWithChildren } from "react";
import { createTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { LinkProps } from "@mui/material/Link";

// Neutral grey used for low-emphasis glyphs (e.g. the unassigned-owner em dash
// and inline edit pencils). Colors live in the theme rather than as hardcoded
// hex constants; reference via the "neutral.main" palette path.
// Must be `interface` (not `type`) — TypeScript only merges augmentations via interfaces.
declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface Palette {
    neutral: Palette["primary"];
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
  }
}

type LinkBehaviorProps = PropsWithChildren<{
  href: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  role?: string;
  tabIndex?: number;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>;
  "aria-current"?: React.AriaAttributes["aria-current"];
  "data-cy"?: string;
}>;

// Only forward DOM-safe props — MUI ButtonBase also spreads ownerState / slots
// that RR7 Link must not receive (can throw "X is not a function" on navigate).
const LinkBehavior =
  // eslint-disable-next-line react/display-name
  React.forwardRef<HTMLAnchorElement, LinkBehaviorProps>((props, ref) => {
    const {
      href,
      id,
      children,
      className,
      style,
      role,
      tabIndex,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      "aria-current": ariaCurrent,
      "data-cy": dataCy,
    } = props;
    return (
      <RouterLink
        ref={ref}
        to={href}
        id={id}
        className={className}
        style={style}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-current={ariaCurrent}
        data-cy={dataCy}
      >
        {children}
      </RouterLink>
    );
  });

const navy = "#32445a" as const;
const overridings = {
  name: "Light Theme",
  typography: {
    fontFamily: "\"Open Sans\",\"Helvetica Neue\",Helvetica,Arial,sans-serif;",
  },
  palette: {
    primary: {
      main: "#2eb6c8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: navy,
    },
    error: {
      main: "#A3030A",
    },
    neutral: {
      main: "#8F8F8F",
    },
  },
  props: {
    MuiTextField: {
      variant: "outlined" as const,
    },
  },
  components: {
    MuiSwitch: {
      defaultProps: {
        color: "secondary" as const,
      },
    },
    // This is needed to override bootstrap's button styles
    // can be removed once bootstrap is removed
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": { textDecoration: "none" },
          "&:focus": { outline: "none", boxShadow: "none", textDecoration: "none" },
          "&:focus-visible": { outline: "none", boxShadow: "none", textDecoration: "none" },
          "&.Mui-focusVisible": { outline: "none", boxShadow: "none", textDecoration: "none" },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "secondary" as const,
      },
    },
    MuiRadio: {
      defaultProps: {
        color: "secondary" as const,
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          textTransform: "initial" as const,
          fontWeight: 600,
          "&:hover": { textDecoration: "none" },
          "&:focus": { outline: "none", boxShadow: "none", textDecoration: "none" },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "initial" as const,
          fontWeight: 600,
          "&:hover": { textDecoration: "none" },
          "&:focus": { outline: "none", boxShadow: "none", textDecoration: "none" },
        },
        containedPrimary: {
          color: "#ffffff",
          backgroundColor: "#2eb6c8",
          "&:hover": {
            backgroundColor: "#25a3b4",
          },
          "&.Mui-disabled": {
            color: "#ffffff",
            backgroundColor: "rgba(46, 182, 200, 0.5)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
          "&.Mui-disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.03)",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid grey",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          "&:hover": { textDecoration: "none" },
          "&:focus": { outline: "none", boxShadow: "none", textDecoration: "none" },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: {
          // bootstrap override
          margin: 0,
        },
      },
    },
  },
  overrides: {
    MuiContainer: {
      maxWidthXl: {
        maxWidth: "1380px",
      },
    },
    MuiTypography: {
      root: {
        color: navy,
      },
    },
    MuiTable: {
      root: {
        minWidth: 450,
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
      },
      head: {
        fontWeight: 600,
      },
    },
    MuiTableRow: {
      root: {
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
          },
        },
      },
    },
    MuiDialogTitle: {
      root: {
        margin: 0,
      },
    },
    MuiInputBase: {
      root: {
        backgroundColor: "white",
        "&$disabled": {
          backgroundColor: "rgba(0, 0, 0, 0.03)",
        },
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: "white",
        },
      },
    },
    MuiMenuItem: {
      root: {
        "&$selected": {
          backgroundColor: "rgba(47, 181, 199, 0.2)",
          color: "#32445a",
        },
      },
    },
    MuiListItem: {
      root: {
        "&$selected": {
          backgroundColor: "rgba(47, 181, 199, 0.2)",
          color: "#32445a",
        },
      },
    },
    MuiAutocomplete: {
      inputRoot: {
        backgroundColor: "white",
      },
      // https://github.com/mui-org/material-ui/issues/19692
      option: {
        "&[data-focus=\"true\"]": {
          backgroundColor: "#effafa",
        },
        "&[aria-selected=\"true\"]": {
          backgroundColor: "#effafa",
        },
      },
      tag: {
        flexDirection: "row !important",
        flex: "none !important",
      },
    },
    MuiPickersMonthSelection: {
      container: {
        flexDirection: "column",
      },
    },
    MuiDialog: {
      paperWidthSm: {
        maxWidth: "800px",
      },
    },
  },
};

export default createTheme(overridings);
